import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plane, Check, CalendarDays, Eye, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { flightsApi, focusSetApi } from "@/lib/api";
import type { AlternateDate, Flight } from "@/lib/types";
import { formatTime, formatPrice } from "../itinerary/itinerary-utils";

/* The one clear "Change flight" affordance per direction (Feature 2). Lists:
   - the pickable FLIGHTS for the current focus route (set the preferred flight),
   - the other date/airport variants as read-only "also watching" rows.
   Picking a flight calls PUT /routes/:routeId/focus-set/preferred, then
   invalidates trip-price + itinerary so the leg re-renders. Delightful + small —
   not a wall of options. v1 changes the FLIGHT within the focus date; other
   dates show as read-only context (no "make primary" endpoint yet). */

interface ChangeFlightDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tripId: string;
  /** The current focus route for this direction. */
  routeId: string;
  /** "Outbound" / "Return" — for the dialog title. */
  directionLabel: string;
  /** Currently-chosen flight number, if any (to mark the selected row). */
  currentFlightNumber?: string | null;
  /** Other date/airport variants in this direction (read-only "also watching"). */
  alternateDates?: AlternateDate[];
}

function FlightOptionRow({
  flight,
  selected,
  pending,
  onPick,
}: {
  flight: Flight;
  selected: boolean;
  pending: boolean;
  onPick: () => void;
}) {
  const dep = formatTime(flight.departure_time);
  const arr = formatTime(flight.arrival_time);
  const price = formatPrice(flight.current_price ?? flight.latest_price ?? flight.lowest_price);
  const stops =
    flight.stops === 0 ? "Nonstop" : flight.stops + (flight.stops === 1 ? " stop" : " stops");

  return (
    <button
      type="button"
      onClick={onPick}
      disabled={pending || selected}
      className={
        "flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors disabled:cursor-default " +
        (selected
          ? "border-[hsl(var(--primary))]/50 bg-[hsl(var(--primary))]/[0.08]"
          : "border-border bg-card hover:border-[hsl(var(--primary))]/40 hover:bg-[hsl(var(--primary))]/[0.04]")
      }
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--primary))]/[0.08] text-[hsl(var(--accent-foreground))]">
        {pending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        ) : (
          <Plane className="h-3.5 w-3.5 -rotate-45" aria-hidden="true" />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="font-semibold text-foreground">{flight.airline || "Flight"}</span>
          <span className="font-mono text-muted-foreground">{flight.flight_number}</span>
          {selected && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-[hsl(var(--accent))] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--accent-foreground))]">
              <Check className="h-2.5 w-2.5" aria-hidden="true" /> Picked
            </span>
          )}
        </span>
        <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          {dep && arr && <span className="font-mono">{dep} → {arr}</span>}
          <span>{stops}</span>
        </span>
      </span>
      {price && (
        <span className="shrink-0 font-mono text-sm font-semibold tabular-nums text-foreground">
          {price}
        </span>
      )}
    </button>
  );
}

export default function ChangeFlightDialog({
  open,
  onOpenChange,
  tripId,
  routeId,
  directionLabel,
  currentFlightNumber,
  alternateDates,
}: ChangeFlightDialogProps) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["flights", routeId, "price", "asc"],
    queryFn: () => flightsApi.getByRoute(routeId, { sort: "price", order: "asc" }),
    enabled: open && !!routeId,
  });
  const flights: Flight[] = data?.flights ?? [];

  const setPreferred = useMutation({
    mutationFn: (flightId: string) => focusSetApi.setPreferred(routeId, flightId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tripPrice", tripId] });
      queryClient.invalidateQueries({ queryKey: ["itinerary", tripId] });
      queryClient.invalidateQueries({ queryKey: ["focusSet", routeId] });
      onOpenChange(false);
    },
    // No onError handler that closes the dialog: on failure React Query resets
    // isPending (spinner stops, selection re-enables) and flips isError — we
    // render an inline error row below the list and leave the dialog open.
  });

  // Other date variants — exclude the one we're already focused on (same route).
  const otherDates = (alternateDates ?? []).filter((d) => d.routeId !== routeId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Change your {directionLabel.toLowerCase()} flight</DialogTitle>
          <DialogDescription>
            Pick a different flight on this date. We'll track the one you choose.
          </DialogDescription>
        </DialogHeader>

        <div className="grid max-h-[55vh] gap-2 overflow-y-auto py-1">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading flights…
            </div>
          ) : flights.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No flights found for this date yet.
            </p>
          ) : (
            flights.map((f) => (
              <FlightOptionRow
                key={f.id}
                flight={f}
                selected={
                  !!currentFlightNumber && f.flight_number === currentFlightNumber
                }
                pending={
                  setPreferred.isPending && setPreferred.variables === f.id
                }
                onPick={() => setPreferred.mutate(f.id)}
              />
            ))
          )}

          {/* Inline failure feedback — keeps the dialog open, selection stays
              enabled (isPending has reset), and the user can simply pick again.
              Reuses the red verdict styling. */}
          {setPreferred.isError && (
            <div className="flex items-start gap-1.5 rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs text-red-700">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
              <span>
                {setPreferred.error instanceof Error && setPreferred.error.message
                  ? setPreferred.error.message
                  : "Couldn't update your flight. Please try again."}
              </span>
            </div>
          )}

          {otherDates.length > 0 && (
            <div className="mt-1 space-y-1.5 border-t border-border pt-3">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <Eye className="h-3 w-3" aria-hidden="true" /> Also watching
              </p>
              {otherDates.map((d) => {
                const price = formatPrice(d.lowestPrice);
                return (
                  <div
                    key={d.routeId}
                    className="flex items-center gap-2.5 rounded-lg border border-dashed border-border bg-muted/30 px-3 py-2 text-sm"
                  >
                    <CalendarDays
                      className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1 truncate text-muted-foreground">
                      {d.date || "Flexible date"}
                      <span className="ml-1.5 font-mono text-xs">
                        {d.origin}→{d.destination}
                      </span>
                    </span>
                    {price && (
                      <span className="shrink-0 text-xs font-medium tabular-nums text-muted-foreground">
                        from <span className="font-mono">{price}</span>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
